import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  ChevronLeft, 
  ChevronDown, 
  ChevronUp,
  TrendingUp, 
  Users, 
  Clock, 
  MapPin,
  Building2,
  Coffee,
  Leaf,
  Star,
  Shield,
  Check,
  Info,
  AlertTriangle,
  Calendar,
  Percent,
  Euro,
  Award
} from "lucide-react";
import { COLORS, CITIES } from "./InvestOverviewPage";

// ═══════════════════════════════════════════════════════════
// INVESTMENT LEVELS
// ═══════════════════════════════════════════════════════════
const INVESTMENT_LEVELS = [
  {
    id: "seed",
    name: "Seed Founder",
    emoji: "🌱",
    amount: 100,
    description: "Der erste Schritt in deine Franky's Beteiligung",
    benefits: [
      "Gewinnbeteiligung anteilig",
      "Digitale Founder-Urkunde",
      "Investor-Updates per E-Mail",
    ],
    color: "#10B981",
  },
  {
    id: "sprout",
    name: "Sprout Founder",
    emoji: "🌿",
    amount: 250,
    description: "Für engagierte Unterstützer mit Weitblick",
    benefits: [
      "Alle Seed Vorteile",
      "10% Rabatt auf alle Produkte",
      "Einladung zur Eröffnungsfeier",
    ],
    color: "#34D399",
    popular: false,
  },
  {
    id: "growth",
    name: "Growth Founder",
    emoji: "🌳",
    amount: 500,
    description: "Die beliebteste Wahl unserer Community",
    benefits: [
      "Alle Sprout Vorteile",
      "15% Rabatt auf alle Produkte",
      "Exklusiver Founder Hoodie",
      "Name auf der Founder Wall",
    ],
    color: "#059669",
    popular: true,
  },
  {
    id: "bloom",
    name: "Bloom Founder",
    emoji: "🌸",
    amount: 1000,
    description: "Für überzeugte Investoren mit Vision",
    benefits: [
      "Alle Growth Vorteile",
      "20% Rabatt auf alle Produkte",
      "VIP Einladung zum Soft Opening",
      "Persönliches Dankesschreiben",
    ],
    color: "#F59E0B",
  },
  {
    id: "harvest",
    name: "Harvest Founder",
    emoji: "🏆",
    amount: 2500,
    description: "Premium-Beteiligung für langfristige Partner",
    benefits: [
      "Alle Bloom Vorteile",
      "25% Rabatt auf alle Produkte",
      "Quartalsweise Investor Calls",
      "Exklusives Founder Dinner",
    ],
    color: "#EF4444",
  },
  {
    id: "innercircle",
    name: "Inner Circle",
    emoji: "👑",
    amount: 5000,
    description: "Exklusiver Zugang zum engsten Kreis",
    benefits: [
      "Alle Harvest Vorteile",
      "30% Rabatt lebenslang",
      "Direkter Kontakt zum Gründerteam",
      "Einfluss auf Store-Gestaltung",
      "VIP Status in allen Filialen",
    ],
    color: "#8B5CF6",
  },
];

// ═══════════════════════════════════════════════════════════
// FAQ DATA
// ═══════════════════════════════════════════════════════════
const FAQS = [
  {
    q: "Was passiert mit meinem Geld?",
    a: "Dein Investment fließt direkt in den Aufbau der Filiale – von der Ausstattung über die Einrichtung bis zur Eröffnung. Du finanzierst echte Assets."
  },
  {
    q: "Wie erhalte ich meine Gewinnbeteiligung?",
    a: "Die Gewinnausschüttung erfolgt jährlich nach Feststellung des Jahresabschlusses. Du erhältst deinen Anteil direkt auf dein hinterlegtes Bankkonto."
  },
  {
    q: "Wie bleibe ich über die Entwicklung informiert?",
    a: "Du erhältst regelmäßige Updates per E-Mail und in der App. Je nach Investment-Level auch quartalsweise Calls oder persönliche Briefings."
  },
  {
    q: "Wann eröffnet der Store?",
    a: "Die geplante Eröffnung ist für Q2 2025 vorgesehen. Nach erfolgreichem Funding beginnt sofort die Bauphase."
  },
  {
    q: "Kann ich mein Investment erhöhen?",
    a: "Ja, solange das Funding läuft, kannst du jederzeit nachinvestieren und damit auch in ein höheres Founder-Level aufsteigen."
  },
  {
    q: "Was passiert, wenn das Funding-Ziel nicht erreicht wird?",
    a: "Sollte das Funding-Ziel nicht erreicht werden, erhältst du dein gesamtes Investment vollständig zurück."
  },
];

// ═══════════════════════════════════════════════════════════
// KÖLN SPECIFIC DATA
// ═══════════════════════════════════════════════════════════
const KOELN_DATA = {
  highlights: [
    { icon: Users, value: "1,1 Mio", label: "Einwohner" },
    { icon: Award, value: "100.000+", label: "Studierende" },
    { icon: Coffee, value: "Top 5", label: "Café-Kultur DE" },
    { icon: Star, value: "6 Mio", label: "Touristen/Jahr" },
  ],
  storeFeatures: [
    { icon: Building2, title: "150m² Store", desc: "Großzügige Fläche im Herzen von Ehrenfeld" },
    { icon: Coffee, title: "Eigene Rösterei", desc: "Frisch gerösteter Specialty Coffee vor Ort" },
    { icon: Leaf, title: "Pasta Bar", desc: "Frische Pasta-Gerichte täglich zubereitet" },
    { icon: Star, title: "Erlebnis-Konzept", desc: "Events, Workshops & Community Space" },
  ],
};

// ═══════════════════════════════════════════════════════════
// LEVEL CARD COMPONENT
// ═══════════════════════════════════════════════════════════
const LevelCard = ({ level, isSelected, onSelect }) => (
  <motion.div
    whileTap={{ scale: 0.98 }}
    onClick={() => onSelect(level)}
    className={`relative p-4 rounded-2xl cursor-pointer transition-all ${isSelected ? 'ring-2' : ''}`}
    style={{ 
      backgroundColor: COLORS.bgCard,
      border: isSelected ? `2px solid ${level.color}` : `1px solid ${COLORS.border}`,
      ringColor: level.color,
    }}
    data-testid={`level-card-${level.id}`}
  >
    {/* Popular Badge */}
    {level.popular && (
      <div 
        className="absolute -top-2 left-4 px-2 py-0.5 rounded-full text-[10px] font-bold"
        style={{ backgroundColor: level.color, color: COLORS.white }}
      >
        Beliebteste Wahl
      </div>
    )}

    <div className="flex items-start gap-3">
      {/* Emoji */}
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
        style={{ backgroundColor: `${level.color}20` }}
      >
        {level.emoji}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-bold" style={{ color: COLORS.textPrimary }}>
            {level.name}
          </h4>
          <span className="font-bold" style={{ color: level.color }}>
            {level.amount.toLocaleString('de-DE')}€
          </span>
        </div>
        <p className="text-xs mb-2" style={{ color: COLORS.textMuted }}>
          {level.description}
        </p>
        
        {/* Benefits Preview */}
        <div className="space-y-1">
          {level.benefits.slice(0, 2).map((benefit, i) => (
            <div key={i} className="flex items-center gap-1">
              <Check className="w-3 h-3 flex-shrink-0" style={{ color: level.color }} />
              <span className="text-xs truncate" style={{ color: COLORS.textSecondary }}>
                {benefit}
              </span>
            </div>
          ))}
          {level.benefits.length > 2 && (
            <span className="text-xs" style={{ color: COLORS.textMuted }}>
              +{level.benefits.length - 2} weitere Vorteile
            </span>
          )}
        </div>
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <div 
          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: level.color }}
        >
          <Check className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  </motion.div>
);

// ═══════════════════════════════════════════════════════════
// FAQ ITEM COMPONENT
// ═══════════════════════════════════════════════════════════
const FAQItem = ({ faq, isOpen, onClick }) => (
  <div 
    className="rounded-xl overflow-hidden"
    style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}
  >
    <button
      onClick={onClick}
      className="w-full p-4 flex items-center justify-between text-left"
    >
      <span className="font-medium text-sm pr-4" style={{ color: COLORS.textPrimary }}>
        {faq.q}
      </span>
      {isOpen ? (
        <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: COLORS.textMuted }} />
      ) : (
        <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: COLORS.textMuted }} />
      )}
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="px-4 pb-4">
            <p className="text-sm" style={{ color: COLORS.textSecondary }}>{faq.a}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// ═══════════════════════════════════════════════════════════
// MAIN INVESTMENT DETAIL PAGE
// ═══════════════════════════════════════════════════════════
export default function InvestmentDetailPage() {
  const { cityId } = useParams();
  const navigate = useNavigate();
  const { user, setShowAuthModal } = useAuth();
  
  const [selectedLevel, setSelectedLevel] = useState(INVESTMENT_LEVELS[2]); // Default: Growth
  const [openFAQ, setOpenFAQ] = useState(null);
  const [showAllLevels, setShowAllLevels] = useState(false);

  // Get city data
  const city = CITIES.find(c => c.id === cityId) || CITIES[0];
  const progress = (city.currentFunding / city.fundingGoal) * 100;

  const handleInvest = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    navigate(`/invest/checkout/${cityId}?level=${selectedLevel.id}`);
  };

  const displayedLevels = showAllLevels ? INVESTMENT_LEVELS : INVESTMENT_LEVELS.slice(0, 4);

  return (
    <div 
      className="min-h-screen pb-32"
      style={{ backgroundColor: COLORS.bgDark }}
    >
      {/* Hero Image */}
      <div className="relative h-56">
        <img 
          src={city.image} 
          alt={city.name}
          className="w-full h-full object-cover"
        />
        <div 
          className="absolute inset-0"
          style={{ background: "linear-gradient(to top, rgba(10,15,28,1) 0%, rgba(10,15,28,0.3) 50%, transparent 100%)" }}
        />
        
        {/* Back Button */}
        <motion.button 
          whileTap={{ scale: 0.9 }} 
          onClick={() => navigate("/invest")}
          className="absolute top-6 left-4 p-2 rounded-xl backdrop-blur-md"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" style={{ color: COLORS.white }} />
        </motion.button>

        {/* Status Badge */}
        <div className="absolute top-6 right-4">
          <span 
            className="px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-md"
            style={{ backgroundColor: "rgba(16,185,129,0.9)", color: COLORS.white }}
          >
            Aktiv · {city.daysLeft} Tage verbleibend
          </span>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          SECTION 1: HERO INFO
          ════════════════════════════════════════════════════════ */}
      <section className="px-5 -mt-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-5 rounded-2xl"
          style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}
        >
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4" style={{ color: COLORS.primary }} />
            <span className="text-sm" style={{ color: COLORS.primary }}>Köln, Deutschland</span>
          </div>
          
          <h1 className="text-2xl font-bold mb-2" style={{ color: COLORS.textPrimary }}>
            Franky's Köln Ehrenfeld
          </h1>
          <p className="text-sm mb-4" style={{ color: COLORS.textSecondary }}>
            Werde Miteigentümer:in unserer neuen Filiale im kreativen Herzen von Köln.
          </p>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl font-bold" style={{ color: COLORS.primary }}>
                {city.currentFunding.toLocaleString('de-DE')}€
              </span>
              <span className="text-sm" style={{ color: COLORS.textMuted }}>
                von {city.fundingGoal.toLocaleString('de-DE')}€
              </span>
            </div>
            <div 
              className="h-3 rounded-full overflow-hidden"
              style={{ backgroundColor: COLORS.bgCardLight }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="h-full rounded-full relative"
                style={{ 
                  background: `linear-gradient(90deg, ${COLORS.primary} 0%, ${COLORS.primaryLight} 100%)` 
                }}
              >
                <div 
                  className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white"
                  style={{ backgroundColor: COLORS.primary }}
                />
              </motion.div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-2 pt-3" style={{ borderTop: `1px solid ${COLORS.border}` }}>
            <div className="text-center">
              <p className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
                {Math.round(progress)}%
              </p>
              <p className="text-xs" style={{ color: COLORS.textMuted }}>Finanziert</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
                {city.investors}
              </p>
              <p className="text-xs" style={{ color: COLORS.textMuted }}>Investor:innen</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold" style={{ color: COLORS.gold }}>
                {city.daysLeft}
              </p>
              <p className="text-xs" style={{ color: COLORS.textMuted }}>Tage übrig</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════
          SECTION 2: WARUM DIESER STANDORT
          ════════════════════════════════════════════════════════ */}
      <section className="px-5 py-6">
        <h2 className="text-lg font-bold mb-4" style={{ color: COLORS.textPrimary }}>
          Warum Köln Ehrenfeld?
        </h2>
        
        <div className="grid grid-cols-2 gap-3">
          {KOELN_DATA.highlights.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="p-4 rounded-xl text-center"
                style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}
              >
                <Icon className="w-5 h-5 mx-auto mb-2" style={{ color: COLORS.primary }} />
                <p className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>{item.value}</p>
                <p className="text-xs" style={{ color: COLORS.textMuted }}>{item.label}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════
          SECTION 3: DER STORE
          ════════════════════════════════════════════════════════ */}
      <section className="px-5 py-4">
        <h2 className="text-lg font-bold mb-4" style={{ color: COLORS.textPrimary }}>
          Der Store
        </h2>
        
        <div className="space-y-3">
          {KOELN_DATA.storeFeatures.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div
                key={i}
                className="p-4 rounded-xl flex items-start gap-3"
                style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}
              >
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${COLORS.primary}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color: COLORS.primary }} />
                </div>
                <div>
                  <p className="font-semibold" style={{ color: COLORS.textPrimary }}>{feature.title}</p>
                  <p className="text-sm" style={{ color: COLORS.textMuted }}>{feature.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════
          SECTION 4: INVESTMENT MODELL
          ════════════════════════════════════════════════════════ */}
      <section className="px-5 py-6">
        <h2 className="text-lg font-bold mb-4" style={{ color: COLORS.textPrimary }}>
          Deine Beteiligung
        </h2>
        
        <div 
          className="p-5 rounded-2xl"
          style={{ 
            background: `linear-gradient(135deg, ${COLORS.bgCard} 0%, ${COLORS.bgAccent} 100%)`,
            border: `1px solid ${COLORS.primary}40`
          }}
        >
          {/* 80/20 Split Visual */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1">
              <div className="flex rounded-lg overflow-hidden h-8">
                <div 
                  className="flex items-center justify-center text-xs font-bold"
                  style={{ width: "80%", backgroundColor: COLORS.bgCardLight, color: COLORS.textMuted }}
                >
                  80% Firma
                </div>
                <div 
                  className="flex items-center justify-center text-xs font-bold"
                  style={{ width: "20%", backgroundColor: COLORS.primary, color: COLORS.white }}
                >
                  20%
                </div>
              </div>
            </div>
          </div>

          <p className="text-sm mb-4" style={{ color: COLORS.textSecondary }}>
            <span className="font-bold" style={{ color: COLORS.primary }}>20% des jährlichen Gewinns</span>{' '}
            dieser Filiale werden anteilig an alle Investor:innen ausgeschüttet.
          </p>

          <p className="text-sm mb-4" style={{ color: COLORS.textMuted }}>
            Je erfolgreicher die Filiale, desto höher dein Anteil.
          </p>

          {/* Example Calculation */}
          <div 
            className="p-4 rounded-xl"
            style={{ backgroundColor: COLORS.bgDark }}
          >
            <p className="text-xs font-semibold mb-2" style={{ color: COLORS.gold }}>
              <Info className="w-3 h-3 inline mr-1" />
              Beispielrechnung
            </p>
            <p className="text-sm" style={{ color: COLORS.textSecondary }}>
              Erwirtschaftet die Filiale <span style={{ color: COLORS.textPrimary }}>50.000€ Gewinn</span>, 
              werden <span style={{ color: COLORS.primary }}>10.000€</span> an die Investor:innen verteilt.
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════
          SECTION 5: INVESTMENT LEVELS
          ════════════════════════════════════════════════════════ */}
      <section className="px-5 py-4">
        <h2 className="text-lg font-bold mb-4" style={{ color: COLORS.textPrimary }}>
          Wähle dein Level
        </h2>
        
        <div className="space-y-3">
          {displayedLevels.map((level) => (
            <LevelCard
              key={level.id}
              level={level}
              isSelected={selectedLevel?.id === level.id}
              onSelect={setSelectedLevel}
            />
          ))}
        </div>

        {!showAllLevels && INVESTMENT_LEVELS.length > 4 && (
          <button
            onClick={() => setShowAllLevels(true)}
            className="w-full mt-3 py-3 rounded-xl text-sm font-medium"
            style={{ 
              backgroundColor: COLORS.bgCard,
              color: COLORS.textSecondary,
              border: `1px solid ${COLORS.border}`
            }}
          >
            Alle {INVESTMENT_LEVELS.length} Levels anzeigen
          </button>
        )}
      </section>

      {/* ════════════════════════════════════════════════════════
          SECTION 6: FAQ
          ════════════════════════════════════════════════════════ */}
      <section className="px-5 py-6">
        <h2 className="text-lg font-bold mb-4" style={{ color: COLORS.textPrimary }}>
          Häufige Fragen
        </h2>
        
        <div className="space-y-2">
          {FAQS.map((faq, i) => (
            <FAQItem
              key={i}
              faq={faq}
              isOpen={openFAQ === i}
              onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
            />
          ))}
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════
          SECTION 7: RISK NOTICE
          ════════════════════════════════════════════════════════ */}
      <section className="px-5 py-4">
        <div 
          className="p-5 rounded-2xl"
          style={{ backgroundColor: `${COLORS.warning}10`, border: `1px solid ${COLORS.warning}30` }}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: COLORS.warning }} />
            <div>
              <p className="font-semibold text-sm mb-2" style={{ color: COLORS.warning }}>
                Wichtiger Risikohinweis
              </p>
              <p className="text-sm" style={{ color: COLORS.textSecondary }}>
                Dies ist eine unternehmerische Beteiligung mit Risiko. 
                Es besteht die Möglichkeit eines vollständigen Verlustes des eingesetzten Kapitals. 
                Eine Rückerstattung ist nach erfolgreichem Funding nicht vorgesehen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════
          SECTION 8: INVESTOR DASHBOARD PREVIEW
          ════════════════════════════════════════════════════════ */}
      <section className="px-5 py-4">
        <h2 className="text-lg font-bold mb-4" style={{ color: COLORS.textPrimary }}>
          So sieht dein Dashboard aus
        </h2>
        
        <div 
          className="p-5 rounded-2xl"
          style={{ 
            backgroundColor: COLORS.bgCard, 
            border: `1px solid ${COLORS.border}`,
          }}
        >
          <div className="flex items-center gap-3 mb-4 pb-4" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center text-xl"
              style={{ backgroundColor: `${selectedLevel.color}20` }}
            >
              {selectedLevel.emoji}
            </div>
            <div>
              <p className="font-bold" style={{ color: COLORS.textPrimary }}>
                {selectedLevel.name}
              </p>
              <p className="text-sm" style={{ color: COLORS.textMuted }}>
                Köln Ehrenfeld
              </p>
            </div>
            <div className="ml-auto text-right">
              <p className="font-bold" style={{ color: selectedLevel.color }}>
                {selectedLevel.amount.toLocaleString('de-DE')}€
              </p>
              <p className="text-xs" style={{ color: COLORS.textMuted }}>investiert</p>
            </div>
          </div>

          {/* Status Items */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: COLORS.textMuted }}>Status</span>
              <span 
                className="px-2 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: `${COLORS.gold}20`, color: COLORS.gold }}
              >
                Funding läuft
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: COLORS.textMuted }}>Geplante Eröffnung</span>
              <span className="text-sm font-medium" style={{ color: COLORS.textPrimary }}>Q2 2025</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: COLORS.textMuted }}>Gewinnbeteiligung</span>
              <span className="text-sm font-medium" style={{ color: COLORS.primary }}>Aktiv nach Eröffnung</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: COLORS.textMuted }}>Nächste Ausschüttung</span>
              <span className="text-sm font-medium" style={{ color: COLORS.textPrimary }}>Q1 2026 (geschätzt)</span>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════
          STICKY CTA
          ════════════════════════════════════════════════════════ */}
      <div 
        className="fixed bottom-0 left-0 right-0 p-4 backdrop-blur-md"
        style={{ 
          backgroundColor: "rgba(10,15,28,0.95)",
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <div className="flex items-center gap-3">
          {/* Selected Level Summary */}
          <div className="flex-1">
            <p className="text-xs" style={{ color: COLORS.textMuted }}>Ausgewählt</p>
            <p className="font-bold" style={{ color: COLORS.textPrimary }}>
              {selectedLevel.emoji} {selectedLevel.name} · {selectedLevel.amount.toLocaleString('de-DE')}€
            </p>
          </div>
          
          {/* CTA Button */}
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleInvest}
            className="px-6 py-3 rounded-xl font-bold"
            style={{ 
              backgroundColor: COLORS.primary,
              color: COLORS.white,
            }}
            data-testid="invest-cta-btn"
          >
            Jetzt investieren
          </motion.button>
        </div>
      </div>
    </div>
  );
}

export { INVESTMENT_LEVELS, FAQS };
